<script lang="ts">
  import { clickOutside } from '$src/utils/clickOutside.util';
  import { slide } from 'svelte/transition';
  import TextInputWithRightButton from '../inputs/textInputs/TextInputWithRightButton.svelte';
  import DefaultDatepicker from '../datepickers/Default.svelte';
  import CalendarWithDate from '$icons/CalendarWithDateIcon.svelte';
  import Close from '$icons/CloseAltIcon.svelte';
  import { createEventDispatcher, onMount } from 'svelte';

  export let label;
  export let selectedDate: Date = new Date();
  export let caption = '';
  export let danger: boolean = false;
  export let dangerMessage: string = '';
  export let dropdownAlign: 'left' | 'right' | 'center' = 'right';
  export let dropdownOpen = false;
  export let inputFocused = false;

  export let value: string = '';

  const dispatch = createEventDispatcher();

  let savedDate: Date = null;

  let placeholder = 'DD / MM / YYYY';

  $: {
    dispatch('change', { value, selectedDate });
    if (value == '01 / 01 / 1970') {
      value = '';
    }
  }

  function convertToDate(dateString: string) {
    const parts = dateString.split('/').map(p => parseInt(p, 10));
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  $: {
    if (value == 'Invalid Date') {
      danger = true;
      dangerMessage = 'This is an invalid date';
    } else {
      danger = false;
    }
  }

  $: dropdownClicked = false;

  $: {
    dropdownOpen = inputFocused || dropdownClicked;
  }

  const isSameDate = (date1, date2) => {
    return (
      date1.getFullYear() === date2?.getFullYear() &&
      date1.getMonth() === date2?.getMonth() &&
      date1.getDate() === date2?.getDate()
    );
  };

  onMount(() => {
    if (!selectedDate) return;
    value = new Date(selectedDate).toLocaleDateString().split('/').join(' / ');
  });
</script>

<div
  class="relative w-full"
  use:clickOutside
  on:click_outside={() => {
    dropdownOpen = false;
    inputFocused = false;
  }}
>
  <TextInputWithRightButton
    bind:label
    bind:caption
    bind:value
    bind:placeholder
    bind:danger
    bind:dangerMessage
    bind:inputFocused
    on:blur={() => {
      if (selectedDate?.toString() === 'Invalid Date') return;

      if (!value && savedDate) {
        selectedDate = savedDate;
        return;
      }

      const newDate = convertToDate(value);
      if (newDate?.toString() === 'Invalid Date') {
        return;
      }

      selectedDate = newDate;
    }}
    on:focus={() => {
      if (!selectedDate || selectedDate.toString() === 'Invalid Date') return;

      savedDate = selectedDate;
      selectedDate = null;
      value = new Date(savedDate).toLocaleDateString().split('/').join(' / ');
    }}
    on:submit
  >
    <button
      class="flex flex-row justify-center items-center cursor-pointer select-none"
      on:click|stopPropagation={() => {
        dropdownOpen = !dropdownOpen;
        //inputFocused = dropdownOpen;
      }}
      on:keydown
    >
      {#if !dropdownOpen}
        <CalendarWithDate />
      {:else}
        <Close size="24" color="#0F9D58" />
      {/if}
    </button>
  </TextInputWithRightButton>
  {#if dropdownOpen}
    <div
    role="button" 
tabindex="0"
      class="dropdown mx-auto"
      class:right-0={dropdownAlign === 'right' || dropdownAlign === 'center'}
      class:left-0={dropdownAlign === 'left' || dropdownAlign === 'center'}
      class:-mt-4={!caption}
      class:mt-1={caption}
      on:keydown
      on:click={() => {
        dropdownClicked = true;
      }}
      use:clickOutside
      on:click_outside={() => {
        dropdownClicked = false;
      }}
      in:slide|global={{ duration: 200 }}
      out:slide|global={{ duration: 200 }}
    >
      <DefaultDatepicker
        year={savedDate?.getFullYear()}
        month={savedDate?.getMonth()}
        bind:selectedDate
        on:select={e => {
          value = new Date(selectedDate).toLocaleDateString().split('/').join(' / ');

          if (!(e.detail.old && e.detail.new)) return;
          if (isSameDate(e.detail.old, e.detail.new)) {
            savedDate = selectedDate;
            selectedDate = null;
            value = '';
          }
        }}
      />
    </div>
  {/if}
</div>

<style type="postcss">
  .dropdown {
    @apply overflow-y-auto mt-2 absolute z-50 bg-white rounded-2xl text-sm font-medium flex flex-col;
    box-shadow:
      0px 12px 16px -4px rgba(16, 24, 40, 0.08),
      0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  }
</style>
